import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowRight, ShieldCheck, Award, Wrench, HeartPulse, Microscope, Beaker, Activity } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { api } from "@/lib/api";

const HERO_IMG = "https://images.unsplash.com/photo-1639772823907-a716be4bdecc?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjV8MHwxfHNlYXJjaHw0fHxtaWNyb3Njb3BlJTIwbGFib3JhdG9yeXxlbnwwfHx8fDE3ODkzMTA3NDB8MA&ixlib=rb-4.1.0&q=85";

const CATEGORIES = [
  { icon: Microscope, name: "Microscopy" },
  { icon: Beaker, name: "Biochemistry" },
  { icon: Activity, name: "Diagnostics" },
  { icon: HeartPulse, name: "Patient Care" },
  { icon: ShieldCheck, name: "Sterilization" },
  { icon: Wrench, name: "Service & Spares" },
];

export default function Home() {
  const { t } = useTranslation();
  const [products, setProducts] = useState([]);
  useEffect(() => { api.get("/products").then(r => setProducts(r.data.slice(0, 3))); }, []);

  return (
    <div>
      {/* HERO */}
      <section className="relative overflow-hidden border-b border-slate-200">
        <div className="absolute inset-0 -z-10 bg-gradient-to-br from-white via-teal-50/40 to-blue-50/60" aria-hidden="true" />
        <div className="mx-auto grid max-w-7xl grid-cols-1 gap-12 px-6 py-16 lg:grid-cols-12 lg:py-24">
          <div className="lg:col-span-7">
            <Badge className="mb-5 rounded-full border border-teal-200 bg-teal-50 px-3 py-1 text-xs font-semibold uppercase tracking-[0.2em] text-teal-700 hover:bg-teal-50" data-testid="hero-badge">
              <Award className="mr-1.5 h-3.5 w-3.5" /> {t("hero.badge")}
            </Badge>
            <h1 className="text-4xl font-bold leading-[1.05] tracking-tight text-[#0F2942] sm:text-5xl lg:text-6xl" style={{ fontFamily: "Outfit, sans-serif" }}>
              {t("hero.title")}
            </h1>
            <p className="mt-6 max-w-xl text-lg leading-relaxed text-slate-600">{t("hero.subtitle")}</p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/products">
                <Button data-testid="hero-products-btn" size="lg" className="bg-[#0F2942] text-white hover:bg-[#0A192F]">
                  {t("hero.ctaProducts")} <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
              <Link to="/contact">
                <Button data-testid="hero-enquiry-btn" size="lg" variant="outline" className="border-[#0F2942] text-[#0F2942] hover:bg-[#0F2942] hover:text-white">
                  {t("hero.ctaEnquiry")}
                </Button>
              </Link>
            </div>
            <div className="mt-12 grid max-w-lg grid-cols-3 gap-6 border-t border-slate-200 pt-8">
              {[
                { n: "1000+", l: t("hero.stats.labs") },
                { n: "100%", l: t("hero.stats.certified") },
                { n: "24/7", l: t("hero.stats.support") },
              ].map((s, i) => (
                <div key={i}>
                  <div className="text-3xl font-bold text-[#0F2942]" style={{ fontFamily: "Outfit, sans-serif" }}>{s.n}</div>
                  <div className="mt-1 text-xs font-medium uppercase tracking-wider text-slate-500">{s.l}</div>
                </div>
              ))}
            </div>
          </div>
          <div className="relative lg:col-span-5">
            <div className="relative overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-xl shadow-slate-200/50">
              <img src={HERO_IMG} alt="Laboratory equipment" className="h-[420px] w-full object-cover" />
              <div className="absolute bottom-4 left-4 right-4 rounded-xl border border-white/10 bg-[#0F2942]/95 p-4 text-white backdrop-blur">
                <div className="text-xs font-semibold uppercase tracking-widest text-teal-300">Trusted Since 2015</div>
                <div className="mt-1 text-lg font-bold" style={{ fontFamily: "Outfit, sans-serif" }}>Pathology · Diagnostics · Research · Hospitals</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* TRUST STRIP */}
      <section className="border-b border-slate-200 bg-white py-10">
        <div className="mx-auto max-w-7xl px-6 text-center">
          <div className="text-xs font-semibold uppercase tracking-[0.25em] text-slate-500">{t("trust.title")}</div>
          <div className="mt-6 flex flex-wrap items-center justify-center gap-6">
            {["Hospitals", "Pathology Labs", "Research Centers", "Universities", "Blood Banks", "Diagnostic Chains"].map((n) => (
              <div key={n} className="rounded-full border border-slate-200 bg-slate-50 px-5 py-2 text-sm font-medium text-slate-700">{n}</div>
            ))}
          </div>
        </div>
      </section>

      {/* CATEGORIES */}
      <section className="py-20">
        <div className="mx-auto max-w-7xl px-6">
          <div className="max-w-2xl">
            <div className="text-xs font-semibold uppercase tracking-[0.2em] text-teal-700">Catalog</div>
            <h2 className="mt-2 text-3xl font-bold tracking-tight text-[#0F2942] sm:text-4xl" style={{ fontFamily: "Outfit, sans-serif" }}>{t("categories.title")}</h2>
            <p className="mt-3 text-slate-600">{t("categories.subtitle")}</p>
          </div>
          <div className="mt-10 grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-6">
            {CATEGORIES.map(({ icon: Icon, name }) => (
              <div key={name} className="group rounded-xl border border-slate-200 bg-white p-6 transition-all hover:-translate-y-1 hover:border-[#0F2942] hover:shadow-lg">
                <Icon className="h-6 w-6 text-teal-600 transition-colors group-hover:text-[#0F2942]" />
                <div className="mt-4 text-sm font-semibold text-[#0F2942]">{name}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURED PRODUCTS */}
      <section className="border-y border-slate-200 bg-slate-50/60 py-20">
        <div className="mx-auto max-w-7xl px-6">
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div>
              <div className="text-xs font-semibold uppercase tracking-[0.2em] text-teal-700">In Stock</div>
              <h2 className="mt-2 text-3xl font-bold tracking-tight text-[#0F2942] sm:text-4xl" style={{ fontFamily: "Outfit, sans-serif" }}>{t("featured.title")}</h2>
              <p className="mt-2 text-slate-600">{t("featured.subtitle")}</p>
            </div>
            <Link to="/products" className="text-sm font-semibold text-[#0F2942] hover:text-teal-700">View all →</Link>
          </div>
          <div className="mt-10 grid gap-6 md:grid-cols-3">
            {products.map((p) => (
              <Link key={p.id} to={`/products/${p.id}`} data-testid={`featured-product-${p.id}`}
                    className="group overflow-hidden rounded-xl border border-slate-200 bg-white transition-all hover:-translate-y-1 hover:shadow-xl">
                <div className="aspect-[4/3] overflow-hidden bg-slate-100">
                  {p.images?.[0] && <img src={p.images[0]} alt={p.name} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />}
                </div>
                <div className="p-5">
                  <div className="text-xs font-semibold uppercase tracking-wider text-teal-700">{p.category}</div>
                  <h3 className="mt-2 line-clamp-2 text-base font-semibold text-[#0F2942]">{p.name}</h3>
                  <div className="mt-4 flex items-center justify-between">
                    <span className="text-sm font-bold text-[#0F2942]">
                      {p.price_on_request ? t("product.por") : `₹ ${p.price?.toLocaleString("en-IN")}`}
                    </span>
                    <span className="text-xs font-semibold text-teal-700">Details →</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20">
        <div className="mx-auto max-w-5xl px-6">
          <div className="overflow-hidden rounded-2xl border border-slate-200 bg-[#0F2942] p-10 text-white md:p-14">
            <div className="flex flex-col items-start justify-between gap-6 md:flex-row md:items-center">
              <div>
                <div className="text-xs font-semibold uppercase tracking-[0.25em] text-teal-300">Ready when you are</div>
                <h2 className="mt-3 text-2xl font-bold sm:text-3xl" style={{ fontFamily: "Outfit, sans-serif" }}>Get a GST quotation within 24 hours</h2>
                <p className="mt-2 max-w-xl text-slate-300">Share your requirement — we'll respond with pricing, availability and delivery timeline.</p>
              </div>
              <Link to="/contact"><Button data-testid="cta-contact-btn" size="lg" className="bg-teal-500 text-white hover:bg-teal-600">Request Quote</Button></Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
